import { useStore } from 'react-redux';

import { useAppDispatch } from 'features/app/hooks';
import type { RootState } from 'features/app/store/store';
import {
  hearingsSelector,
  pendingDisconnectById,
  setHearingLoadingStateById,
} from 'features/call/call-hearing/store';
import { handleError } from 'features/notification/store';
import { useVoiceMeetingSession } from 'features/voice-meeting/hooks';
import { CHIME_LOG_GROUP } from 'features/voice-meeting/constants';
import { setVoiceSessionLoading } from 'features/voice-session/store';

export const useHearingDisconnect = () => {
  const dispatch = useAppDispatch();
  const store = useStore<RootState>();
  const { disconnectParticipants } = useVoiceMeetingSession();

  const disconnectMultipleHearings = async (participantIds: string[]) => {
    let disconnectResults: boolean[] = [];

    const stateAtClick = store.getState();
    const hearings = hearingsSelector(stateAtClick);
    const clickSet = new Set(participantIds);
    // The click empties the voice session iff no hearing OUTSIDE the click set
    // is still connecting/connected.
    const willEmptySession = !hearings.some(
      (h) =>
        !clickSet.has(h.id) &&
        (h.status === 'connecting' || h.status === 'connected')
    );

    console.log('HearingDebug: disconnectMultipleHearings', {
      participantIds,
      willEmptySession,
    });

    try {
      if (willEmptySession) {
        dispatch(setVoiceSessionLoading(true));
      }

      participantIds.forEach((participantId) => {
        dispatch(pendingDisconnectById({ id: participantId }));
        dispatch(
          setHearingLoadingStateById({ isLoading: true, participantId })
        );
      });

      disconnectResults = await disconnectParticipants(participantIds);
      console.log('HearingDebug: disconnectParticipants results', {
        participantIds,
        disconnectResults,
      });
    } catch (error) {
      console.log('HearingDebug: disconnectMultipleHearings failed', {
        participantIds,
        error,
      });
      dispatch(
        handleError({
          error,
          methodName: 'disconnectMultipleHearings',
          logGroup: CHIME_LOG_GROUP,
        })
      );
      disconnectResults = participantIds.map(() => false);
    }
    // Per-hearing state (pendingDisconnect, isLoading) is now cleared by:
    //  - acknowledged path: setHearingStatus{,WithId} reducer on 'disconnected'
    //  - no-ack path: useHearingTeardownWatcher via forceHearingTeardown
    // Voice-session-wide loading is cleared by the downstream session-end
    // cleanup (resetVoiceSession), driven by either path above when this click
    // empties the session.

    return disconnectResults;
  };

  const disconnectHearingFromChimeMeeting = (participantId: string) => {
    return disconnectMultipleHearings(new Array(participantId));
  };

  return {
    disconnectMultipleHearings,
    disconnectHearingFromChimeMeeting,
  };
};
